import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchProjectAssetObjectUrl } from '../services/projectAssets';
import { actionFailureMessage } from '../utils/actionFailure';
import { IconPlay } from './Icons';
import InlineError from './InlineError';

interface AudioPreviewProps {
  projectId: string;
  assetId: string;
  name: string;
}

export default function AudioPreview({ projectId, assetId, name }: AudioPreviewProps) {
  const { t } = useTranslation(['common']);
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);
  const unmounted = useRef(false);

  useEffect(() => {
    unmounted.current = false;
    return () => {
      unmounted.current = true;
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    };
  }, []);

  const play = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const objectUrl = await fetchProjectAssetObjectUrl(projectId, assetId);
      if (unmounted.current) {
        URL.revokeObjectURL(objectUrl);
        return;
      }
      urlRef.current = objectUrl;
      setUrl(objectUrl);
    } catch (e) {
      if (!unmounted.current) {
        setError(actionFailureMessage(e, t('assets.audio.previewFailed', 'Could not load this sound. Please try again.')));
      }
    } finally {
      if (!unmounted.current) setIsLoading(false);
    }
  };

  if (url) {
    return (
      <audio
        controls
        autoPlay
        src={url}
        aria-label={t('assets.audio.player', '{{name}} preview', { name })}
        style={{ width: '100%', height: 32, marginTop: 6 }}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6, alignItems: 'flex-start' }}>
      <button
        type="button"
        className="btn sm"
        onClick={play}
        disabled={isLoading}
        aria-label={t('assets.audio.play', 'Play {{name}}', { name })}
      >
        <IconPlay size={12} />
        {isLoading ? t('assets.audio.loading', 'Loading…') : t('assets.audio.playLabel', 'Play')}
      </button>
      {error && <InlineError>{error}</InlineError>}
    </div>
  );
}
